import { IModalState, IRootState, LoadingModalType, NotificationPayload } from "@/interfaces/state"
import { ActionContext } from "vuex"
import { Module } from "vuex"
import { EventType, trackEvent } from "@/helpers/AmplitudeHelper"
import router from "@/router"

type modalPayload = {
  showModal: boolean
}

const modalModule: Module<IModalState, IRootState> = {
  state(): IModalState {
    return {
      showAuthModal: false,
      showNotificationsModal: false,
      showLanguageDropdown: false,
      showNotification: false,
      notificationMessage: null,
      notificationIsClosing: false,
      showMobileFiltersModal: false,
      loadingModalType: "beforePayment",
      showSignUpReportLoadingModal: false,
      showSignUpReportSuccessModal: false,
      showSignUpReportFailureModal: false,
      showSignUpReportCustomerModal: false,
      showSignUpReportCancelledModal: false,
      showSetupTariffsModal: false,
    }
  },
  getters: {
    showAuthModal(state: IModalState) {
      return state.showAuthModal
    },
    showNotificationsModal(state: IModalState) {
      return state.showNotificationsModal
    },
    showLanguageDropdown(state: IModalState) {
      return state.showLanguageDropdown
    },
    showNotification(state: IModalState) {
      return state.showNotification
    },
    notificationMessage(state: IModalState) {
      return state.notificationMessage
    },
    notificationIsClosing(state: IModalState) {
      return state.notificationIsClosing
    },
    showMobileFiltersModal(state: IModalState) {
      return state.showMobileFiltersModal
    },
    loadingModalType(state: IModalState) {
      return state.loadingModalType
    },
    showSignUpReportLoadingModal(state: IModalState) {
      return state.showSignUpReportLoadingModal
    },
    showSignUpReportSuccessModal(state: IModalState) {
      return state.showSignUpReportSuccessModal
    },
    showSignUpReportFailureModal(state: IModalState) {
      return state.showSignUpReportFailureModal
    },
    showSignUpReportCustomerModal(state: IModalState) {
      return state.showSignUpReportCustomerModal
    },
    showSignUpReportCancelledModal(state: IModalState) {
      return state.showSignUpReportCancelledModal
    },
    showSetupTariffsModal(state: IModalState) {
      return state.showSetupTariffsModal
    }
  },
  mutations: {
    setAuthModal(state: IModalState, payload: modalPayload) {
      state.showAuthModal = payload.showModal
    },
    setNotificationsModal(state: IModalState, payload: modalPayload) {
      state.showNotificationsModal = payload.showModal
    },
    setLanguageDropdown(state: IModalState, payload: modalPayload) {
      state.showLanguageDropdown = payload.showModal
    },
    setShowNotification(state: IModalState, payload: modalPayload) {
      state.showNotification = payload.showModal
    },
    setNotificationMessage(state: IModalState, payload: NotificationPayload | null) {
      state.notificationMessage = payload
    },
    setNotificationIsClosing(state: IModalState, payload: modalPayload) {
      state.notificationIsClosing = payload.showModal
    },
    setMobileFiltersModal(state: IModalState, payload: modalPayload) {
      state.showMobileFiltersModal = payload.showModal
    },
    setLoadingModalType(state: IModalState, payload: LoadingModalType) {
      state.loadingModalType = payload
    },
    setSignUpReportLoadingModal(state: IModalState, payload: modalPayload) {
      state.showSignUpReportLoadingModal = payload.showModal
    },
    setSignUpReportSuccessModal(state: IModalState, payload: modalPayload) {
      state.showSignUpReportSuccessModal = payload.showModal
    },
    setSignUpReportFailureModal(state: IModalState, payload: modalPayload) {
      state.showSignUpReportFailureModal = payload.showModal
    },
    setSignUpReportCustomerModal(state: IModalState, payload: modalPayload) {
      state.showSignUpReportCustomerModal = payload.showModal
    },
    setSignUpReportCancelledModal(state: IModalState, payload: modalPayload) {
      state.showSignUpReportCancelledModal = payload.showModal
    },
    setSetupTariffsModal(state: IModalState, payload: modalPayload) {
      state.showSetupTariffsModal = payload.showModal
    },
  },
  actions: {
    toggleAuthModal(context: ActionContext<IModalState, IModalState>) {
      const modalState = context.getters.showAuthModal
      if (modalState === false) {
        document.body.classList.add("is-popup-opened");
        trackEvent(EventType.login_popup_opened)
      } else {
        trackEvent(EventType.login_popup_close)
      }
      if (router.currentRoute.value.name === 'tenderPage') {
        context.dispatch('setIntendedRoute', router.currentRoute.value.fullPath)
      }
      context.commit('setAuthModal', { showModal: !modalState })
    },
    toggleLanguageDropdown(context: ActionContext<IModalState, IModalState>) {
      context.commit('setLanguageDropdown', { showModal: !context.getters.showLanguageDropdown })
    },
    toggleNotificationsModal(context: ActionContext<IModalState, IModalState>) {
      const modalState = context.getters.showNotificationsModal
      if (modalState === false) {
        document.body.classList.add("is-popup-opened")
        context.commit('setNotificationsModal', { showModal: true })
      } else {
        document.body.classList.add("is-popup-closed");
        setTimeout(() => {
          document.body.classList.remove("is-popup-opened");
          document.body.classList.remove("is-popup-closed");
          context.commit('setNotificationsModal', { showModal: false })
        }, 250);
      }
    },
    openNotification(context: ActionContext<IModalState, IModalState>, payload: NotificationPayload) {
      context.commit('setShowNotification', { showModal: true })
      context.commit('setNotificationMessage', payload)
      setTimeout(() => {
        context.dispatch('closeNotification')
      }, 10000)
    },
    closeNotification(context: ActionContext<IModalState, IModalState>) {
      context.commit('setNotificationIsClosing', { showModal: true })
      setTimeout(() => {
        context.commit('setNotificationIsClosing', { showModal: false })
        context.commit('setShowNotification', { showModal: false })
        context.commit('setNotificationMessage', null)
      }, 210)
    },
    toggleMobileFiltersModal(context: ActionContext<IModalState, IModalState>) {
      const modalState = context.getters.showMobileFiltersModal
      if (modalState === false) {
        document.body.classList.add("is-popup-opened")
        context.commit('setMobileFiltersModal', { showModal: true })
      } else {
        document.body.classList.add("is-popup-closed");
        setTimeout(() => {
          document.body.classList.remove("is-popup-opened");
          document.body.classList.remove("is-popup-closed");
          context.commit('setMobileFiltersModal', { showModal: false })
        }, 250);
      }
    },
    toggleSignUpReportLoadingModal(context: ActionContext<IModalState, IModalState>) {
      context.commit('setLoadingModalType', "beforePayment")
      const modalState = context.getters.showSignUpReportLoadingModal
      if (modalState === false) {
        document.body.classList.add("is-popup-opened");
      }
      context.commit('setSignUpReportLoadingModal', { showModal: !modalState })
    },
    togglePaymentProcessingModal(context: ActionContext<IModalState, IModalState>) {
      context.commit('setLoadingModalType', "processingPayment")
      const modalState = context.getters.showSignUpReportLoadingModal
      if (modalState === false) {
        document.body.classList.add("is-popup-opened");
      }
      context.commit('setSignUpReportLoadingModal', { showModal: !modalState })
    },
    toggleSignUpReportSuccessModal(context: ActionContext<IModalState, IModalState>) {
      const modalState = context.getters.showSignUpReportSuccessModal
      if (modalState === false) {
        document.body.classList.add("is-popup-opened");
      }
      context.commit('setSignUpReportSuccessModal', { showModal: !modalState })
    },
    toggleSignUpReportFailureModal(context: ActionContext<IModalState, IModalState>) {
      const modalState = context.getters.showSignUpReportFailureModal
      if (modalState === false) {
        document.body.classList.add("is-popup-opened");
      }
      context.commit('setSignUpReportFailureModal', { showModal: !modalState })
    },
    toggleSignUpReportCustomerModal(context: ActionContext<IModalState, IModalState>) {
      const modalState = context.getters.showSignUpReportCustomerModal
      if (modalState === false) {
        document.body.classList.add("is-popup-opened");
      }
      context.commit('setSignUpReportCustomerModal', { showModal: !modalState })
    },
    toggleSignUpReportCancelledModal(context: ActionContext<IModalState, IModalState>) {
      const modalState = context.getters.showSignUpReportCancelledModal
      if (modalState === false) {
        document.body.classList.add("is-popup-opened");
      }
      context.commit('setSignUpReportCancelledModal', { showModal: !modalState })
    },
    toggleSetupTariffsModal(context: ActionContext<IModalState, IModalState>) {
      const modalState = context.getters.showSetupTariffsModal
      if (modalState === false) {
        document.body.classList.add("is-popup-opened");
      }
      context.commit('setSetupTariffsModal', { showModal: !modalState })
    },
  }
}

export default modalModule
